import React, { useState, useEffect, CSSProperties } from 'react';
import { PlusIcon, TrashIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Check, ChevronDown } from 'lucide-react';
import { aqlVisionApiUrl, publicAnonKey } from "/utils/supabase/info";
import { toast } from 'sonner';
import { useTranslation } from '@/app/hooks/useTranslation';
import { Button } from '@/app/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@/app/components/ui/sheet';

interface Rule {
  id: string;
  sensorId: string;
  sensorName?: string;
  zoneId?: string;
  parameter: string;
  condition: '>' | '<' | '>=' | '<=' | '==' | '!=' | 'between';
  threshold: number;
  thresholdMax?: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  isActive: boolean;
}

interface AlertRuleManagerProps {
  client?: any;
  project?: any;
  zones?: any[];
}

const SEVERITY_COLORS = {
  critical: { dot: 'bg-red-500', badge: 'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300', label: 'Crítica' },
  high: { dot: 'bg-orange-500', badge: 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300', label: 'Alta' },
  medium: { dot: 'bg-amber-500', badge: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300', label: 'Média' },
  low: { dot: 'bg-sky-500', badge: 'border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300', label: 'Baixa' }
};

const PARAMETER_OPTIONS = [
  { value: 'temperature', label: 'Temperatura' },
  { value: 'ph', label: 'pH' },
  { value: 'dissolved_oxygen', label: 'O2 dissolvido' },
  { value: 'redox', label: 'Redox / ORP' },
  { value: 'salinity', label: 'Salinidade' },
  { value: 'turbidity', label: 'Turbidez' }
];

const CONDITION_OPTIONS = [
  { value: '>', label: 'Maior que' },
  { value: '<', label: 'Menor que' },
  { value: '>=', label: 'Maior ou igual' },
  { value: '<=', label: 'Menor ou igual' },
  { value: '==', label: 'Igual' },
  { value: '!=', label: 'Diferente' },
  { value: 'between', label: 'Entre limites' }
] as const;

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Crítica' }
] as const;

const controlChrome = 'border-border bg-muted/35 hover:border-border/90 hover:bg-muted/55 dark:border-white/7 dark:bg-slate-950/18 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] dark:hover:border-white/12 dark:hover:bg-slate-950/24';
const focusChrome = 'focus:border-primary/55 focus:bg-background focus:ring-2 focus:ring-primary/12 dark:focus:bg-slate-950/24';
const inputClass = `h-10 w-full rounded-lg border px-3.5 text-sm text-foreground outline-none transition-all ${controlChrome} ${focusChrome}`;
const textAreaClass = `w-full rounded-lg border px-3.5 py-2.5 text-sm leading-6 text-foreground outline-none transition-all ${controlChrome} ${focusChrome}`;
const choiceButtonClass = `group flex min-h-10 w-full items-center justify-between gap-3 rounded-lg border px-3.5 py-2 text-left text-sm text-foreground outline-none transition-all ${controlChrome} ${focusChrome} cursor-pointer`;
const choicePanelClass = 'mt-2 overflow-hidden rounded-lg border border-border bg-popover/98 p-1 shadow-xl shadow-slate-950/25 backdrop-blur';

const truthyStatus = (value: unknown, fallback = true) => {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined || value === '') return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'active', 'ativo', 'ativa', 'enabled', 'on', 'ok'].includes(normalized)) return true;
  if (['false', '0', 'inactive', 'inativo', 'inativa', 'disabled', 'off'].includes(normalized)) return false;
  return fallback;
};

const normalizeRule = (row: any): Rule => ({
  id: String(row?.id ?? row?.rule_id ?? ''),
  sensorId: String(row?.sensorId ?? row?.sensor_id ?? row?.metadata?.sensor_id ?? row?.metadata?.sensorId ?? 'Simulador'),
  sensorName: row?.sensorName ?? row?.sensor_name ?? row?.metadata?.sensor_name ?? row?.metadata?.sensorName,
  zoneId: row?.zoneId ?? row?.zone_id ?? undefined,
  parameter: String(row?.parameter ?? row?.sensor_type ?? row?.metric ?? 'temperature'),
  condition: (row?.condition === '=' ? '==' : row?.condition ?? '>') as Rule['condition'],
  threshold: Number(row?.threshold ?? row?.limit ?? 0),
  thresholdMax: row?.thresholdMax ?? row?.threshold_max ?? row?.max_threshold,
  severity: (row?.severity ?? 'medium') as Rule['severity'],
  message: String(row?.message ?? ''),
  isActive: truthyStatus(row?.isActive ?? row?.is_active ?? row?.enabled ?? row?.active, true)
});

function StyledChoice({
  id,
  value,
  placeholder,
  options,
  openChoiceKey,
  setOpenChoiceKey,
  onChange
}: {
  id: string;
  value: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  openChoiceKey: string | null;
  setOpenChoiceKey: React.Dispatch<React.SetStateAction<string | null>>;
  onChange: (value: string) => void;
}) {
  const selectedOption = options.find((option) => option.value === value);
  const open = openChoiceKey === id;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpenChoiceKey((key) => key === id ? null : id)}
        className={choiceButtonClass}
      >
        <span className={`truncate font-medium ${selectedOption ? 'text-foreground' : 'text-muted-foreground'}`}>
          {selectedOption?.label ?? placeholder ?? 'Selecionar...'}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <div className={choicePanelClass}>
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpenChoiceKey(null);
                }}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  active ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-muted/45 hover:text-foreground'
                }`}
              >
                <span className="truncate">{option.label}</span>
                {active ? <Check className="h-4 w-4 text-primary" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function AlertRuleManager(props: AlertRuleManagerProps) {
  const { t: translate } = useTranslation();
  const tr = (key: string) => translate('configuration', key);
  const clientId = String(props.client?.client_id ?? props.client?.id ?? '').trim();
  const projectId = String(props.project?.project_id ?? props.project?.id ?? '').trim();
  
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('edit');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [openChoiceKey, setOpenChoiceKey] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Rule>>({
    sensorId: 'Simulador',
    parameter: 'temperature',
    condition: '>',
    threshold: 24,
    severity: 'high',
    message: '',
    isActive: true
  });

  const fetchRules = async () => {
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      if (projectId) params.set('project_id', projectId);
      const res = await fetch(`${aqlVisionApiUrl}/vision-rules${params.toString() ? `?${params.toString()}` : ''}`, {
        headers: { Authorization: `Bearer ${publicAnonKey}` }
      });
      const data = await res.json();
      setRules((data.rules || []).map(normalizeRule));
    } catch (err) {
      toast.error(tr('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, [clientId, projectId]);

  const openNew = () => {
    setMode('create');
    setFormData({
      sensorId: 'Simulador',
      parameter: 'temperature',
      condition: '>',
      threshold: 24,
      severity: 'high',
      message: '',
      isActive: true
    });
    setSelectedRule(null);
    setOpenChoiceKey(null);
    setSheetOpen(true);
  };

  const openEdit = (rule: Rule) => {
    setMode('edit');
    setSelectedRule(rule);
    setFormData(rule);
    setOpenChoiceKey(null);
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.sensorId || !formData.message) {
      toast.error(tr('fillAllFields'));
      return;
    }
    if (!clientId) {
      toast.error('Cliente em falta para guardar a regra.');
      return;
    }

    const normalizedCondition = formData.condition === '==' ? '=' : formData.condition;
    const payload = {
      rule_id: selectedRule?.id,
      client_id: clientId,
      project_id: projectId || null,
      zone_id: formData.zoneId ?? null,
      sensor_type: formData.parameter ?? 'temperature',
      sensor_id: formData.sensorId,
      condition: normalizedCondition,
      threshold: Number(formData.threshold),
      severity: formData.severity ?? 'medium',
      enabled: formData.isActive ?? true,
      message: formData.message ?? null,
      metadata: {
        sensor_id: formData.sensorId,
        sensor_name: formData.sensorName ?? formData.sensorId,
        threshold_max: formData.thresholdMax ?? null
      }
    };

    try {
      if (mode === 'create') {
        const res = await fetch(`${aqlVisionApiUrl}/vision-rules`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}` 
          },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          toast.success(tr('ruleCreated'));
          setSheetOpen(false);
          fetchRules();
        }
      } else if (selectedRule) {
        const res = await fetch(`${aqlVisionApiUrl}/vision-rules/${selectedRule.id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}` 
          },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          toast.success(tr('ruleSaved'));
          setSheetOpen(false);
          fetchRules();
        }
      }
    } catch (err) {
      toast.error(tr('saveFailed'));
    }
  };

  const handleDelete = async (ruleId: string) => {
    try {
      const res = await fetch(`${aqlVisionApiUrl}/vision-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${publicAnonKey}` }
      });
      if (res.ok) {
        toast.success(tr('ruleDeleted'));
        fetchRules();
        setDeleteConfirm(null);
      }
    } catch (err) {
      toast.error(tr('deleteFailed'));
    }
  };

  const filtered = rules.filter((rule) =>
    query.toLowerCase() === '' ||
    rule.parameter.toLowerCase().includes(query.toLowerCase()) ||
    rule.message.toLowerCase().includes(query.toLowerCase()) ||
    String(rule.sensorId ?? '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-3">
      <style>
        {`
          @keyframes aqlSectionCardIn {
            0% {
              opacity: 0;
              transform: translate3d(0, 16px, 0) scale(0.99);
              filter: blur(10px);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
              filter: blur(0);
            }
          }

          .aql-config-section-card {
            animation: aqlSectionCardIn 620ms cubic-bezier(0.22, 1, 0.36, 1) both;
          }

          @keyframes aqlRowIn {
            0% {
              opacity: 0;
              transform: translate3d(0, 10px, 0);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0);
            }
          }

          .aql-config-table-row {
            animation: aqlRowIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
            transition: background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
          }

          .aql-config-table-row:hover {
            transform: translateY(-1px);
            background: linear-gradient(90deg, rgba(56, 189, 248, 0.05), rgba(15, 23, 42, 0.02));
          }

          @media (prefers-reduced-motion: reduce) {
            .aql-config-section-card,
            .aql-config-table-row {
              animation: none !important;
            }

            .aql-config-table-row:hover {
              transform: none !important;
            }
          }
        `}
      </style>

      {/* Header */}
      <section className="aql-adaptive-card aql-config-header aql-config-section-card rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Regras de Alerta</p>
            <h2 className="mt-1 text-lg font-semibold text-foreground">Automação de alertas por sensor</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Os mínimos, máximos e valores alvo do sensor descrevem a faixa operacional. Estas regras definem quando uma leitura recebida pelo edge device cria um alerta e um caso para validação.
            </p>
          </div>
          <Button
            onClick={openNew}
            className="inline-flex items-center gap-2 bg-cyan-500 text-white hover:bg-cyan-400 dark:text-black"
          >
            <PlusIcon className="h-4 w-4" />
            Nova regra
          </Button>
        </div>
      </section>

      {/* Search & Toolbar */}
      <section className="aql-adaptive-card aql-config-header aql-config-section-card rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar regras..."
              className="w-full rounded-xl border border-border bg-muted/30 py-2.5 pl-10 pr-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
            />
          </div>
          <div className="text-sm text-muted-foreground">{filtered.length} regra(s)</div>
        </div>
      </section>

      {/* Rules Grid */}
      {loading ? (
        <div className="rounded-2xl border border-border bg-muted/10 py-12 text-center">
          <p className="text-sm text-muted-foreground">Carregando regras...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/10 py-12 text-center">
          <p className="text-lg font-medium text-foreground">Sem regras</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {rules.length === 0 ? 'Cria a primeira regra de alerta' : 'Nenhuma regra corresponde à pesquisa'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((rule, index) => (
            <button
              key={rule.id}
              type="button"
              onClick={() => openEdit(rule)}
              className="aql-config-table-row group min-h-[300px] rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:border-primary/50 hover:bg-card cursor-pointer dark:bg-card/70 dark:hover:bg-card/90"
              style={{ animationDelay: `${120 + index * 32}ms` } as CSSProperties}
            >
              <div className="flex h-full flex-col space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      {rule.parameter}
                    </p>
                    <h3 className="mt-1 truncate text-lg font-bold text-foreground">
                      {rule.condition} {rule.threshold}
                      {rule.condition === 'between' && rule.thresholdMax ? ` - ${rule.thresholdMax}` : ''}
                    </h3>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <span className="text-xs font-semibold text-muted-foreground">Mensagem</span>
                    <p className="line-clamp-2 text-sm font-medium text-foreground/80">{rule.message}</p>
                  </div>

                  <div className="space-y-1.5">
                    <span className="text-xs font-semibold text-muted-foreground">Sensor</span>
                    <p className="text-sm font-medium text-foreground/80">{rule.sensorId || 'Simulador'}</p>
                  </div>
                </div>

                <div className="mt-auto border-t border-border/50 pt-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${SEVERITY_COLORS[rule.severity].badge}`}>
                      {SEVERITY_COLORS[rule.severity].label}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(rule.id);
                      }}
                      className="rounded-lg border border-red-300 bg-red-50 p-2 text-red-700 transition-colors hover:bg-red-100 hover:text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20 dark:hover:text-red-200"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {!rule.isActive && (
                    <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                      Inativa
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full gap-0 border-l border-border bg-card text-foreground sm:max-w-[680px]">
          <SheetHeader className="border-b border-border px-5 py-4 pr-12">
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {mode === 'create' ? 'Nova regra' : 'Editar regra'}
            </SheetTitle>
            <SheetDescription className="mt-1 max-w-[56ch] text-sm leading-5">
              {mode === 'create'
                ? 'Define quando uma leitura de edge device deve gerar alerta operacional e caso de validação.'
                : 'Atualiza os parâmetros que controlam a geração de alertas por sensor.'}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
            <div className="space-y-2.5">
              <label className="block text-[13px] font-semibold leading-none text-foreground">Sensor</label>
              <input
                placeholder="Identificação do sensor"
                className={inputClass}
                value={formData.sensorId || ''}
                onChange={(e) => setFormData({ ...formData, sensorId: e.target.value })}
              />
            </div>

            <div className="space-y-2.5">
              <label className="block text-[13px] font-semibold leading-none text-foreground">Parâmetro</label>
              <StyledChoice
                id="parameter"
                value={String(formData.parameter ?? '')}
                placeholder="Selecionar..."
                options={PARAMETER_OPTIONS}
                openChoiceKey={openChoiceKey}
                setOpenChoiceKey={setOpenChoiceKey}
                onChange={(value) => setFormData({ ...formData, parameter: value })}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2.5">
                <label className="block text-[13px] font-semibold leading-none text-foreground">Condição</label>
                <StyledChoice
                  id="condition"
                  value={String(formData.condition ?? '')}
                  options={[...CONDITION_OPTIONS]}
                  openChoiceKey={openChoiceKey}
                  setOpenChoiceKey={setOpenChoiceKey}
                  onChange={(value) => setFormData({ ...formData, condition: value as Rule['condition'] })}
                />
              </div>

              <div className="space-y-2.5">
                <label className="block text-[13px] font-semibold leading-none text-foreground">Limite</label>
                <input
                  type="number"
                  className={inputClass}
                  value={formData.threshold || ''}
                  onChange={(e) => setFormData({ ...formData, threshold: Number(e.target.value) })}
                />
              </div>

              {formData.condition === 'between' && (
                <div className="space-y-2.5">
                  <label className="block text-[13px] font-semibold leading-none text-foreground">Limite máximo</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={formData.thresholdMax || ''}
                    onChange={(e) => setFormData({ ...formData, thresholdMax: Number(e.target.value) })}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2.5">
              <label className="block text-[13px] font-semibold leading-none text-foreground">Severidade</label>
              <StyledChoice
                id="severity"
                value={String(formData.severity ?? '')}
                options={[...SEVERITY_OPTIONS]}
                openChoiceKey={openChoiceKey}
                setOpenChoiceKey={setOpenChoiceKey}
                onChange={(value) => setFormData({ ...formData, severity: value as Rule['severity'] })}
              />
            </div>

            <div className="space-y-2.5">
              <label className="block text-[13px] font-semibold leading-none text-foreground">Mensagem</label>
              <textarea
                placeholder="Ex: Temperatura > 24°C"
                className={textAreaClass}
                rows={3}
                value={formData.message || ''}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              />
            </div>

            <div className="space-y-2.5">
              <label className="block text-[13px] font-semibold leading-none text-foreground">Estado</label>
              <button
                type="button"
                role="switch"
                aria-checked={formData.isActive ?? true}
                onClick={() => setFormData({ ...formData, isActive: !(formData.isActive ?? true) })}
                className={`flex w-full items-center justify-between gap-4 rounded-lg border px-3.5 py-2 text-left transition-all ${controlChrome} ${focusChrome} cursor-pointer`}
              >
                <span className={`text-sm font-semibold ${(formData.isActive ?? true) ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground'}`}>
                  {(formData.isActive ?? true) ? 'Ativa' : 'Inativa'}
                </span>
                <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${(formData.isActive ?? true) ? 'bg-sky-400/45' : 'bg-slate-700/70'}`}>
                  <span className={`absolute top-1 h-4 w-4 rounded-full shadow-sm transition-transform ${(formData.isActive ?? true) ? 'translate-x-6 bg-sky-50' : 'translate-x-1 bg-slate-300/80'}`} />
                </span>
              </button>
            </div>
          </div>

          <SheetFooter className="grid gap-3 border-t border-border px-5 py-4 sm:grid-cols-2">
            <Button
              variant="outline"
              onClick={() => setSheetOpen(false)}
              className="h-11 rounded-xl text-sm"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              className="h-11 rounded-xl bg-cyan-500 text-sm font-semibold text-black hover:bg-cyan-400"
            >
              Guardar
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="max-w-sm rounded-2xl border border-red-500/35 bg-card p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-2 text-red-300">
                <ExclamationTriangleIcon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground">Apagar regra?</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Esta ação é permanente e não pode ser desfeita.
                </p>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200"
              >
                Apagar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
